import React, { lazy, Suspense } from "react";
import { Route, Routes } from "react-router-dom";
import { Watermark } from "antd";
import { SharedValueProvider } from "./components/Common/SharedValueContext";

// ag-grid CSS paths (correct for @27.3.0)
import 'ag-grid-community/dist/styles/ag-grid.css';
import 'ag-grid-community/dist/styles/ag-theme-alpine.css';

// Shells and Global Components (Eager loaded)
import LandingPage from "./components/landing/LandingPage";
import Dashboard from "./components/Dashboard";
import AdminDashboard from "./components/admin/AdminDashboard";

// Lazy Loaded Route Components
const DashboardComp = lazy(() => import("./components/dashboard/DashboardComp"));
const HighYieldPage = lazy(() => import("./components/dashboard/HighYieldPage"));
const AboutPage = lazy(() => import("./components/landing/AboutPage"));
const HomePage = lazy(() => import("./components/landing/HomePage"));
const LandingMenu = lazy(() => import("./components/landing/LandingMenu"));
const LoginSignUp = lazy(() => import("./components/landing/LoginSignUp"));
const MockTestHome = lazy(() => import("./components/landing/MockTestHome"));
const QuestionBankHome = lazy(() => import("./components/landing/QuestionBankHome"));
const Terms = lazy(() => import("./components/landing/Terms"));
const ContactAuth = lazy(() => import("./components/student/ContactAuth"));
const PerformancePage = lazy(() => import("./components/student/performance/PerformancePage"));
const Invoices = lazy(() => import("./components/student/profile/Invoices"));
const Profile = lazy(() => import("./components/student/profile/Profile"));
const QbExam = lazy(() => import("./components/student/QbExam"));
const SessionReview = lazy(() => import("./components/student/quiz/SessionReview"));
const QuizPage = lazy(() => import("./components/student/QuizPage"));
const ResetQuestionsHist = lazy(() => import("./components/student/resetquestion/ResetQuestionsHist"));
const MockTestPage = lazy(() => import("./components/student/mocktest/MockTestPage"));
const PrivacyPolicy = lazy(() => import("./components/landing/PrivacyPolicy"));
const CopyRights = lazy(() => import("./components/landing/CopyRights"));
const AdminQBAnswers = lazy(() => import("./components/admin/pages/AdminQBAnswers"));
const AdminLanding = lazy(() => import("./components/admin/AdminLanding"));
const AdminQuestion = lazy(() => import("./components/admin/pages/AdminQuestion"));
const AdminTextBook = lazy(() => import("./components/admin/pages/AdminTextBook"));
const AdminImportCSV = lazy(() => import("./components/admin/pages/AdminImportCSV"));
const AdminHighYieldPage = lazy(() => import("./components/admin/pages/AdminHighYieldPage"));
const AdminHYTopic = lazy(() => import("./components/admin/pages/AdminHYTopic"));
const AdminHYCategory = lazy(() => import("./components/admin/pages/AdminHYCategory"));
const AdminHYSubCategory = lazy(() => import("./components/admin/pages/AdminHYSubCategory"));
const AdminMockPage = lazy(() => import("./components/admin/pages/mock/AdminMockPage"));
const AdminMockQuestion = lazy(() => import("./components/admin/pages/mock/AdminMockQuestion"));
const AdminMockTextBook = lazy(() => import("./components/admin/pages/mock/AdminMockTextBook"));
const AdminFAQ = lazy(() => import("./components/admin/pages/AdminFAQ"));
const AdminInvoice = lazy(() => import("./components/admin/pages/AdminInvoice"));
const AdminMembers = lazy(() => import("./components/admin/pages/AdminMembers"));
const AdminMainCatPage = lazy(() => import("./components/admin/pages/AdminMainCatPage"));
const AdminCategory = lazy(() => import("./components/admin/pages/AdminCategory"));
const AdminMainCategory = lazy(() => import("./components/admin/pages/AdminMainCategory"));
const AdminSubCategory = lazy(() => import("./components/admin/pages/AdminSubCategory"));
const AdminState = lazy(() => import("./components/admin/pages/AdminState"));
const AdminExam = lazy(() => import("./components/admin/pages/AdminExam"));
const AdminChangePassword = lazy(() => import("./components/admin/pages/AdminChangePassword"));
const AdminSubscription = lazy(() => import("./components/admin/pages/AdminSubscription"));
const AdminHYViewTopics = lazy(() => import("./components/admin/pages/AdminHYViewTopics"));
const AdminMockQBAnswers = lazy(() => import("./components/admin/pages/AdminMockQBAnswers"));
const VeritfyEmail = lazy(() => import("./http/VeritfyEmail"));
const AdminQuesSetLimit = lazy(() => import("./components/admin/pages/AdminQuesSetLimit"));
const AdminMenu = lazy(() => import("./components/admin/widgets/AdminMenu"));
const FaqsPage = lazy(() => import("./components/landing/FaqsPage"));
const Blog = lazy(() => import("./components/landing/Blog"));
const AdminBlogs = lazy(() => import("./components/admin/pages/AdminBlogs"));
const AdminBlogsHeading = lazy(() => import("./components/admin/pages/AdminBlogsHeading"));
const AdminInstruction = lazy(() => import("./components/admin/pages/AdminInstruction").then(m => ({ default: m.AdminInstruction })));
const ArticleFile = lazy(() => import("./components/landing/ArticleFile"));
const AdminRoles = lazy(() => import("./components/admin/pages/AdminRoles"));
const VeritfyBlogEmail = lazy(() => import("./http/VeritfyBlogEmail"));
const BlogUnsubscription = lazy(() => import("./http/BlogUnsubscription"));
const VeritfyComments = lazy(() => import("./http/VeritfyComments"));
const AdminTestimonials = lazy(() => import("./components/admin/pages/testimonials/AdminTestimonials"));

const PageLoader = () => (
  <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
    <p>Loading...</p>
  </div>
);

function App() {
  return (
    <div>
      <SharedValueProvider>
        <Watermark
          content="Test Mode"
          height={32}
          width={165}
          font={{ fontSize: 21 }}
        >
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/" element={<LandingPage />}>
                <Route path="/" element={<HomePage />} />
                <Route path="courses" element={<HomePage />} />
                <Route path="about" element={<AboutPage />} />
                <Route path="faqs" element={<FaqsPage />} />
                <Route path="article" element={<ArticleFile />} />
                <Route path="blog" element={<Blog />} />
                <Route path="blog/welcome" element={<Blog />} />
                <Route path="blog/article/:id" element={<Blog />} />
                <Route path="contact" element={<AboutPage />} />
                <Route path="login" element={<LoginSignUp />} />
                <Route path="terms" element={<Terms />} />
                <Route path="copy-rights" element={<CopyRights />} />
                <Route
                  path="reset-password"
                  element={<LoginSignUp reset={true} />}
                />
                <Route path="privacy-policy" element={<PrivacyPolicy />} />
                <Route path="menu" element={<LandingMenu />} />
                <Route path="questionbank" element={<QuestionBankHome />} />
                <Route
                  path="courses/questionbank"
                  element={<QuestionBankHome />}
                />
                <Route path="courses/mocktest" element={<MockTestHome />} />
                <Route path="mocktest" element={<MockTestHome />} />
              </Route>
              <Route path="/user" element={<Dashboard />}>
                <Route path="dashboard" element={<DashboardComp />} />
                <Route path="menu" element={<LandingMenu type={"afterAuth"} />} />
                <Route path="profile" element={<Profile />} />
              </Route>
              <Route path="/student" element={<Dashboard />}>
                <Route path="mocktest" element={<MockTestPage />} />
                <Route path="qbexam" element={<QbExam />} />
                <Route path="quiz" element={<QuizPage type={"quiz"} />} />
                <Route
                  path="timed-test"
                  element={<QuizPage type={"timed-test"} />}
                />
                <Route path="mock-test" element={<QuizPage type={"mock"} />} />
                <Route path="review" element={<SessionReview />} />
                <Route path="highyield" element={<HighYieldPage />} />
                <Route
                  path="highyield/:categoryParam"
                  element={<HighYieldPage />}
                />
                <Route path="performance" element={<PerformancePage />} />
                <Route path="resetqb" element={<ResetQuestionsHist />} />
                <Route path="contact" element={<ContactAuth />} />
                <Route path="myinvoices" element={<Invoices />} />
              </Route>
              <Route path="/admin" element={<AdminDashboard />}>
                <Route path="menu" element={<AdminMenu />} />
                <Route path="dashboard" element={<AdminLanding />} />
                <Route path="question" element={<AdminQuestion />} />
                <Route path="textbook" element={<AdminTextBook />} />
                <Route
                  path="answers/:answerParams"
                  element={<AdminQBAnswers />}
                />
                <Route
                  path="importcsv"
                  element={<AdminImportCSV type={"qb"} />}
                />
                <Route path="instruction" element={<AdminInstruction />} />

                <Route
                  path="qbsetlimit"
                  element={<AdminQuesSetLimit type={"qb"} />}
                />
                <Route
                  path="mockquestionsetlimit"
                  element={<AdminQuesSetLimit type={"mock"} />}
                />
                <Route path="blogsheading" element={<AdminBlogsHeading />} />
                <Route path="createblogs" element={<AdminBlogs />} />

                <Route path="highyield" element={<AdminHighYieldPage />}>
                  <Route path="topic" element={<AdminHYTopic />} />
                  <Route path="category" element={<AdminHYCategory />} />
                  <Route path="subcategory" element={<AdminHYSubCategory />} />
                  <Route path="viewtopic" element={<AdminHYViewTopics />} />
                </Route>
                <Route path="mock" element={<AdminMockPage />}>
                  <Route path="question" element={<AdminMockQuestion />} />
                  <Route
                    path="answers/:answerParams"
                    element={<AdminMockQBAnswers />}
                  />
                  <Route path="textbook" element={<AdminMockTextBook />} />
                  <Route
                    path="importcsv"
                    element={<AdminImportCSV type={"mock"} />}
                  />
                </Route>
                <Route path="faq" element={<AdminFAQ />} />
                <Route path="invoice" element={<AdminInvoice />} />
                <Route path="members" element={<AdminMembers />} />
                <Route path="roles" element={<AdminRoles />} />
                <Route path="main" element={<AdminMainCatPage />}>
                  <Route path="category" element={<AdminMainCategory />} />
                </Route>
                <Route path="category" element={<AdminCategory />} />
                <Route path="sub-category" element={<AdminSubCategory />} />
                <Route path="state" element={<AdminState />} />
                <Route path="exam" element={<AdminExam />} />
                <Route path="subscription" element={<AdminSubscription />} />
                <Route path="change-password" element={<AdminChangePassword />} />
                <Route path="testimonials" element={<AdminTestimonials />} />
              </Route>
              <Route path="confirm-registration" element={<VeritfyEmail />} />
              <Route path="confirm-email" element={<VeritfyBlogEmail />} />
              <Route path="blog-unSubscribe" element={<BlogUnsubscription />} />
              <Route path="approved-comments" element={<VeritfyComments />} />

              <Route
                path="*"
                element={
                  <main style={{ padding: "1rem" }}>
                    <p>You are not authorised!</p>
                  </main>
                }
              />
            </Routes>
          </Suspense>
        </Watermark>
      </SharedValueProvider>
    </div>
  );
}

export default App;
